import { MultiSelect, ISelectItem } from "@react-gcc-eds/core";
import { useState, useEffect } from "react";
import { useStore } from "../../../../store";
import { MultiSelectLimitedPills } from "../../../shared/multiselect-limited-pills";

export const PurchaseOrdersFilterDropDown = ({
  onChange,
  selectedItems
}: {
  onChange: (selectedItem: ISelectItem<string>[]) => void;
  selectedItems: ISelectItem<string>[];
}) => {
  const [state] = useStore();
  const [noAvailablePurchaseOrderNumbers, setNoAvailablePurchaseOrderNumbers] = useState<boolean>();
  const {
    itemCreationDateBarsAvailablePurchaseOrderNumbers: fetchingPurchaseOrderNumbers,
    itemCreationDateBarsMetric: fetchingItemCreationDateBarsMetrics
  } = state.fetchingData.metrics;

  const disableDropdown =
    noAvailablePurchaseOrderNumbers ||
    fetchingPurchaseOrderNumbers === true ||
    fetchingItemCreationDateBarsMetrics === true;

  useEffect(() => {
    const noAvailablePurchaseOrderNumbers =
      state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers === undefined ||
      state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers.length === 0;

    setNoAvailablePurchaseOrderNumbers(noAvailablePurchaseOrderNumbers);
  }, [
    state.metricsFilters.overallOrderStatusBarsItemsOpCos,
    state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers
  ]);

  return (
    <div className="pill-filter-wrapper">
      <MultiSelect
        key="poDropDown"
        items={
          state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers
            ? (state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers as string[]).map(
                poNumber => ({ value: poNumber, title: poNumber }) as ISelectItem
              )
            : []
        }
        disabled={disableDropdown}
        onChange={(items: ISelectItem[]) => onChange(items)}
        selectedItems={selectedItems}
        placeholder={
          noAvailablePurchaseOrderNumbers
            ? "No Purchase Orders"
            : fetchingPurchaseOrderNumbers || fetchingItemCreationDateBarsMetrics
              ? "Fetching data"
              : "Select Purchase Order"
        }
        searchable
        withPills={false}
      />
      <MultiSelectLimitedPills
        data={selectedItems}
        onRemove={onChange}
        isDisabled={disableDropdown}
      />
    </div>
  );
};

export const OpCoFilterDropDown = ({
  tab,
  onChange,
  selectedItems
}: {
  tab: "order" | "item";
  onChange: (selectedItem: ISelectItem<string>[]) => void;
  selectedItems: ISelectItem<string>[];
}) => {
  const [state] = useStore();
  const [allOpCos, setAllOpcos] = useState<ISelectItem[]>();
  const [availableOpCos, setAvailableOpCos] = useState<ISelectItem[]>();
  const {
    itemCreationDateBarsMetric: fetchingItemCreationDateBarsMetrics,
    orderCreationDateBarsMetric: fetchingOrderCreationDateBarsMetrics
  } = state.fetchingData.metrics;

  const disableDropdown =
    fetchingItemCreationDateBarsMetrics === true || fetchingOrderCreationDateBarsMetrics === true;

  const noSelectedOpCos =
    state.metricsFilters.overallOrderStatusBarsItemsOpCos === undefined ||
    state.metricsFilters.overallOrderStatusBarsItemsOpCos.length === 0;

  const anySelectedPurchaseOrders =
    state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers &&
    state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers.length > 0;

  useEffect(() => {
    const allOpCos = getAllOpcos();
    setAllOpcos(allOpCos);
  }, []);

  useEffect(() => {
    if (tab === "item" && anySelectedPurchaseOrders && noSelectedOpCos) {
      const uniqueCustomerIds = new Set(
        state.metrics.itemCreationDateBarsMetric.map(m => m.customerId)
      );

      const opCos = state.customer
        ? state.customer.accounts
            .map(a => {
              if (uniqueCustomerIds.has(Number(a.accountId)))
                return {
                  value: a.accountId.toString(),
                  title: a.displayName
                };
            })
            .filter(o => o !== undefined)
        : [];

      setAvailableOpCos(opCos);
    } else if (
      !anySelectedPurchaseOrders ||
      availableOpCos === undefined ||
      (anySelectedPurchaseOrders && availableOpCos && availableOpCos.length > 1)
    ) {
      setAvailableOpCos(() => allOpCos);
    }
  }, [state.metrics.itemCreationDateBarsMetric, allOpCos]);

  const getAllOpcos = () =>
    state.customer && state.customer.accounts
      ? state.customer.accounts.map(
          x => ({ value: x.accountId, title: x.displayName }) as ISelectItem
        )
      : [];

  return (
    <div className="pill-filter-wrapper">
      <MultiSelect
        key={`opCoDropDown-${tab}`}
        items={availableOpCos ?? []}
        disabled={disableDropdown}
        onChange={(items: ISelectItem[]) => onChange(items)}
        selectedItems={selectedItems}
        placeholder={disableDropdown ? "Fetching data" : "Search OpCo"}
        searchable
        withPills={false}
      />
      <MultiSelectLimitedPills
        data={selectedItems}
        onRemove={onChange}
        isDisabled={disableDropdown}
      />
    </div>
  );
};
